import { useLazyQuery } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    OMElementLibraryElementType,
    ProjectElementType,
} from '@deltek/specification-client/core/Models/projectElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { GetDropdownElementNodesForOfficeMasterWorkspaceQuery } from 'api/graphQL/queries/projectElements.queries'
import clsx from 'clsx'
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog'
import {
    useActionBarActions,
    useOMDropdownOptions,
    useSelectedOMVersion,
} from 'components/WorkspaceEditor/ActionBarStore'
import {
    PanelType,
    usePanelElementList,
} from 'components/WorkspaceEditor/hooks/usePanelElementList'
import { usePTElementNodesUpdate } from 'components/WorkspaceEditor/hooks/usePTElementNodesUpdate'
import { ExecutionResult } from 'graphql'
import {
    BulkAddElementInput,
    GetOMFamilyIterationAndElementNodesInput,
    NodeSearchFilter,
} from 'models/getProjectElementNodesInput'
import {
    FamilyIteration,
    GetOMProjectElementNodesDropdownResponseForWorkspace,
    ProductFamilies,
} from 'models/projectElementNode'
import {
    CheckForDuplicateFilesInAssemblyInput,
    ImportUploadFileInfo,
} from 'models/projectImport'
import { FamilyNumberFormat } from 'models/projectSettings'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import ElementPanelSearch from 'pages/StudioPage/components/ElementPanelSearch'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import { useLocation } from 'react-router-dom'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useBetaWorkspaceFlag } from 'shared/useBetaWorkspaceFlag'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    getSortedList,
    updatedDivisionElements,
} from 'shared/utils/workspaceUtils'
import { AreaContext } from '../Common/AreaContext'
import { WorkspaceActionBarContext } from '../WorkspaceActionBarContext'
import ElementTreeView, {
    ElementItemDataNode,
    ElementItemIdentifier,
} from './ElementTreeView'
export interface ElementsPanelStyleProps {
    calledFromImportDuplicateFamily?: boolean
    calledFromImportPage?: boolean
    contentTeamFromStudioPage?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
    isOMWorkspace?: boolean
}
const useElementsPanelStyles = makeStyles((theme: Theme) => ({
    elementPanelContentContainer: {
        height: '100%',
        '& > *:not(:last-child)': {
            marginBottom: theme.spacing(1),
        },
        paddingTop: (props: ElementsPanelStyleProps) =>
            props.contentTeamFromStudioPage
                ? theme.spacing(0)
                : props?.calledFromImportPage ||
                  props?.calledFromImportDuplicateFamily
                ? 'inherit'
                : props.isBetaWorkspaceToggleEnabled
                ? theme.spacing(1.5)
                : theme.spacing(6),
    },
    panelTitle: {
        fontSize: pxToRem(14),
        color: theme.palette.primary.dark,
        textTransform: 'uppercase',
        fontWeight: 500,
        padding: theme.spacing(1),
        paddingRight: 0,
    },
    panelTitleImport: {
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
        textTransform: 'uppercase',
        fontWeight: 500,
        padding: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2.5),
        paddingTop: theme.spacing(2),
    },
    panelTitleStudio: {
        color: theme.palette.common.white,
    },
    panelTitleContainerImport: {
        display: 'flex',
        width: 430,
        justifyContent: 'space-between',
    },
    panelTitleContainerStudio: {
        width: '100%',
        alignItems: 'center',
        paddingTop: (props: ElementsPanelStyleProps) =>
            props.isBetaWorkspaceToggleEnabled &&
            !props.contentTeamFromStudioPage
                ? theme.spacing(1)
                : theme.spacing(0),
    },
    panelContentGrid: {
        height: '100%',
    },

    elementTreeViewContainer: {
        flexGrow: 1,
    },
    loadMoreButton: {
        borderRadius: 100,
        fontSize: pxToRem(12),
        backgroundColor: theme.palette.common.white,
        margin: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    emptyElementsListText: {
        fontSize: pxToRem(14),
        color: theme.palette.grey[500],
        padding: theme.spacing(2),
        textAlign: 'center',
    },
    loadingSkeleton: {
        width: '95%',
        height: '50px',
        marginBottom: '20px',
        borderRadius: '5px',
        background: '#586b83',
    },
}))

export interface ElementsPanelProps extends ElementsPanelStyleProps {
    selectedElementIdentifier?: ElementItemIdentifier
    onSelectedItemIdentifierChange?: (
        newSelectedElementIdentifier?: ElementItemIdentifier
    ) => void
    savePreviousElementForPersistance?: (
        elementIdentifier?: ElementItemIdentifier
    ) => void
    elementFlatList?: ProjectElementForImport[] | null
    onLoadMoreClick?: VoidFunction
    handleBPElementTreeChange?: (
        id: string,
        treepath: string,
        isBP: boolean,
        isGBP: boolean,
        remove: boolean
    ) => void
    isLoading?: boolean
    showFullLoadState?: boolean
    isNewDataLoading?: boolean
    totalElementCount?: number
    panelTitle?: string
    famNumberFormat?: FamilyNumberFormat
    searchFilters?: NodeSearchFilter[]
    onLoadChildren: (id: string, isBulkSelectRequested?: boolean) => void
    parentNodeIdPendingBulkSelect?: string
    hasMoreElementsToFetch?: boolean
    redirectKey?: string
    getAssociationLists?: (
        data: CheckForDuplicateFilesInAssemblyInput[],
        isProductType?: boolean,
        removedElementTreePath?: string
    ) => void
    selectedFileData?: Partial<ImportUploadFileInfo> | null
    associations?: CheckForDuplicateFilesInAssemblyInput[]
    showGlobalSnackbar?: (options?: any) => Promise<ExecutionResult<any>>
    projectId?: string
    selectedElementFormat?: string
    contentTeamFromStudioPage?: boolean
    setBPSearchValue?:
        | ((value?: string | boolean | null, panel?: string | null) => void)
        | undefined
    fromStudio?: boolean
    isBPImport?: boolean
    selectedBulkAddElements?: BulkAddElementInput[]
    setSelectedBulkAddElements?: (bulkElements?: BulkAddElementInput[]) => void
    isFromWorkSpacePage?: boolean
    persistedElement?: ElementItemIdentifier
    handleSelectDuplicateFamilyVersion?: (element: ElementItemDataNode) => void
    selectedDuplicateFamilyVersionNodes?: ElementItemDataNode[]
    familyNumber?: string
    familyName?: string
    isOMWorkspace?: boolean
    updateElementList?: (elementList: ProjectElementNode[]) => void
    selectedItemIdFromStudioTable?: string
    panelType?: PanelType
}

export const ProjectElementTypeMap: Record<string, string> = {
    ASSEMBLY: 'Assembly',
    PRODUCTFAMILY: 'Family',
    PRODUCTTYPE: 'Product Type',
}
export interface ProjectElementForImport extends ProjectElementNode {
    isSelectedForAssociation?: boolean
    imoprtedToProjectId?: boolean
    isBasedFromImportedNode?: boolean
    emptyWithoutAssociations?: boolean
    isDeactivated?: boolean
    isFromPPLTemplate?: boolean
}
const ElementsPanel: React.FC<ElementsPanelProps> = ({
    selectedElementIdentifier,
    onSelectedItemIdentifierChange: onSelectedItemIdChange,
    savePreviousElementForPersistance,
    elementFlatList = null,
    onLoadMoreClick,
    handleBPElementTreeChange,
    isLoading = true,
    showFullLoadState = false,
    isNewDataLoading = false,
    totalElementCount = 0,
    panelTitle = 'Project Elements',
    famNumberFormat = FamilyNumberFormat.XXXXXX,
    searchFilters,
    onLoadChildren,
    parentNodeIdPendingBulkSelect,
    hasMoreElementsToFetch,
    redirectKey,
    calledFromImportPage,
    getAssociationLists,
    selectedFileData,
    showGlobalSnackbar,
    projectId,
    selectedElementFormat,
    contentTeamFromStudioPage,
    setBPSearchValue,
    fromStudio,
    isBPImport,
    selectedBulkAddElements,
    setSelectedBulkAddElements,
    isFromWorkSpacePage = false,
    persistedElement,
    calledFromImportDuplicateFamily,
    handleSelectDuplicateFamilyVersion,
    selectedDuplicateFamilyVersionNodes: selectedDuplicateFamilyVersions,
    familyNumber,
    familyName,
    isOMWorkspace,
    updateElementList,
    selectedItemIdFromStudioTable,
    panelType = PanelType.DEFAULT,
}) => {
    const { currentState } = React.useContext(WorkspaceActionBarContext)
    // hook to check if the beta workspace is enabled using url params
    const { isBetaWorkspaceEnabled } = useBetaWorkspaceFlag()
    const { previewFeatures } = usePreviewFeatures()
    // New workspace toggle is enabled when: not in OM workspace (or OM_WORKSPACE feature flag is on),
    // the firm has the workspace preview feature, and the user has toggled it on
    const isBetaWorkspaceToggleEnabled =
        (!isOMWorkspace || previewFeatures.hasOMWorkspace) &&
        (previewFeatures.hasNewWorkspace2 || isBetaWorkspaceEnabled) &&
        currentState?.showHideStates?.betaWorkspace
    const classes = useElementsPanelStyles({
        calledFromImportDuplicateFamily: calledFromImportDuplicateFamily,
        calledFromImportPage: calledFromImportPage,
        contentTeamFromStudioPage: contentTeamFromStudioPage,
        isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
        isOMWorkspace,
    })

    const { pathname } = useLocation()

    const { selectedProjectNode } = React.useContext(AreaContext)

    const { showGlobalSnackbar: showGlobalSnackbarHook } = useGlobalSnackbar()

    const {
        setSelectedElementIdentifier,
        categoryElementStates,
        setCategoryElementStates,
    } = React.useContext(ProjectPageContext)
    const [selectedFile, setSelectedFile] = React.useState<
        Partial<ImportUploadFileInfo> | undefined
    >()

    const [panelWidth, setPanelWidth] = React.useState<number>(0)

    const selectedFamilyId = selectedFile?.familyId?.replace(/['"]+/g, '')
    const [associationList, setAssociationList] = React.useState<
        CheckForDuplicateFilesInAssemblyInput[]
    >([])
    const [
        selectedElementToRemove,
        setSelectedElementToRemove,
    ] = React.useState<ElementItemDataNode | undefined>()
    const [showRemoveDialog, setShowRemoveDialog] = React.useState<boolean>(
        false
    )
    const selectedOMVersion = useSelectedOMVersion()
    const omDropdownOptions = useOMDropdownOptions()
    const { elementList, setElementList } = usePanelElementList(panelType)
    const { setOMDropdownOptions, setSelectedOMVersion } = useActionBarActions()

    const [fetchOMVersionDropdown] = useLazyQuery<
        GetOMProjectElementNodesDropdownResponseForWorkspace | null,
        GetOMFamilyIterationAndElementNodesInput
    >(GetDropdownElementNodesForOfficeMasterWorkspaceQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        onCompleted: (
            data: GetOMProjectElementNodesDropdownResponseForWorkspace | null
        ) => {
            if (data) {
                setOMDropdownOptions(
                    data
                        .getFamilyIterationWithElementTreeNodesForOfficeMasterWorkspace
                        ?.familyIterations
                )
            }
        },
    })

    const confirmationDialogContent = React.useMemo(
        () => (
            <>
                This action will permanently remove associations and properties
                from your project.
            </>
        ),
        []
    )
    const treeStruct: ElementItemDataNode[] | null = React.useMemo(() => {
        const horizontalRef: ElementItemDataNode[] = [{ children: [] }]
        let horizontalIndex = 0
        elementList?.forEach(
            (
                sourceItem: ProjectElementForImport,
                index: number,
                sourceArray: ProjectElementForImport[]
            ) => {
                const nonSelectedPTs = selectedFile?.selectedProductTypeName
                    ? selectedFile?.productTypeInfo?.filter(
                          (item) =>
                              item.name !==
                                  selectedFile?.selectedProductTypeName &&
                              item?.associations &&
                              item?.associations?.length !== 0 &&
                              item?.associations?.filter((itemInner) => {
                                  return (
                                      itemInner?.treePath ===
                                      associationList?.filter(
                                          (itemList) =>
                                              itemList?.treePath ===
                                                  itemInner?.treePath &&
                                              itemInner
                                                  ?.productTypeName?.[0] !==
                                                  selectedFile?.selectedProductTypeName
                                      )?.[0]?.treePath
                                  )
                              })
                      )
                    : []

                const selectedPaths: any =
                    associationList.map((item) => {
                        return item?.treePath
                            ?.split(`|${selectedFamilyId}`)?.[0]
                            .split('||')
                            .map((itemInner) => {
                                return itemInner
                            })
                    }) ?? []
                let siblingAssemblies: string[] = []
                nonSelectedPTs?.map((item) =>
                    item?.associations?.map(
                        (item) =>
                            item && siblingAssemblies?.push(item?.treePath!)
                    )
                )

                const parentAssembliesSelected: string[] = [].concat(
                    ...selectedPaths!
                )!

                const parentForSiblingsSelected: string[] = siblingAssemblies
                    ? [...siblingAssemblies?.map((item) => item!)!]
                    : []

                const isAssembly = sourceItem.elementType === 'ASSEMBLY'
                const isProductType = sourceItem.elementType === 'PRODUCTTYPE'
                // determine nesting level of previous element
                const previousElement = sourceArray[index - 1]
                const previousElementNestingLevel = previousElement
                    ? getElementNestingLevel(
                          previousElement.elementType === 'PRODUCTTYPE'
                              ? ''
                              : previousElement.text.split('|')[0],
                          previousElement.elementType
                      )
                    : 0
                // determine proper nesting level
                const textSplit = sourceItem.text.split('|')
                const subTextCode = isProductType ? '' : textSplit[0]
                const elementTitle = textSplit[textSplit.length - 1]
                let currentItemLevel = getElementNestingLevel(
                    subTextCode,
                    sourceItem.elementType
                )
                const familyId = `${
                    horizontalRef[horizontalIndex].id
                }||${selectedFamilyId}||${selectedFile?.title?.replace(
                    selectedFamilyId!,
                    ''
                )}`

                const selectedPTs = selectedFile?.selectedProductTypeName
                    ? selectedFile?.productTypeInfo?.filter(
                          (item) =>
                              item.name ===
                                  selectedFile?.selectedProductTypeName &&
                              associationList &&
                              associationList?.length !== 0 &&
                              ((item?.associations &&
                                  item?.associations?.filter(
                                      (itemA) =>
                                          itemA?.treePath ===
                                          sourceItem?.treePath
                                  )?.length !== 0) ||
                                  associationList?.filter(
                                      (item2) =>
                                          item2?.treePath ===
                                              sourceItem?.treePath &&
                                          item2?.treePath ===
                                              sourceItem?.treePath
                                  )?.length !== 0)
                      )
                    : selectedFile?.productTypeInfo

                const duplicateImportSubtitle =
                    sourceItem.elementType ===
                    OMElementLibraryElementType.PRODUCTFAMILYBUCKET
                        ? `Family ${subTextCode}`
                        : sourceItem.elementType ===
                              ProjectElementType.PRODUCTFAMILY &&
                          sourceItem.isPrimaryIteration
                        ? 'Primary'
                        : ''

                const currentElementType =
                    ProjectElementTypeMap[`${sourceItem.elementType}`]
                const divisionSubTitle =
                    currentElementType === 'Family'
                        ? 'Section'
                        : currentElementType === 'Assembly'
                        ? 'Division'
                        : currentElementType
                const subtitleText = calledFromImportDuplicateFamily
                    ? duplicateImportSubtitle
                    : `${
                          selectedElementFormat === 'DIVISIONS'
                              ? divisionSubTitle
                              : selectedElementFormat === 'SUSTAINABILITY'
                              ? 'Topic'
                              : currentElementType
                      } ${subTextCode}`
                const isL4L5Assembly =
                    isAssembly && currentItemLevel >= 2 && currentItemLevel <= 3
                const divisionParentTreePath = elementList?.filter(
                    (item) => item?.id === sourceItem?.parentNodeId
                )?.[0]?.treePath
                const elementItem: ElementItemDataNode = {
                    id:
                        selectedElementFormat === 'DIVISIONS' &&
                        sourceItem?.elementType !== 'ASSEMBLY'
                            ? `${divisionParentTreePath}||${sourceItem?.text}`
                            : sourceItem.treePath,
                    parentNodeId: sourceItem?.parentNodeId ?? '',
                    baseElementId: sourceItem.baseElementId,
                    isImportedToProject: sourceItem?.isImportedToProject,
                    importedToProjectId: sourceItem?.importedToProjectId,
                    isBpPublished: sourceItem?.isBpPublished,
                    isDeactivated: sourceItem?.isDeactivated,
                    isBasedFromImportedNode:
                        sourceItem?.isBasedFromImportedNode,
                    selected: false,
                    children:
                        associationList?.findIndex(
                            (item) =>
                                selectedFile?.selectedProductTypeName &&
                                item?.treePath === sourceItem?.treePath
                        ) > -1 ||
                        !sourceItem?.hasChildren ||
                        (nonSelectedPTs?.length !== 0 &&
                            nonSelectedPTs?.filter((item) =>
                                item?.associations?.some(
                                    (itemInner) =>
                                        itemInner?.treePath ===
                                        sourceItem?.treePath
                                )
                            )?.length !== 0 &&
                            sourceItem?.elementType === 'ASSEMBLY')
                            ? [
                                  {
                                      id:
                                          selectedElementFormat === 'DIVISIONS'
                                              ? `${divisionParentTreePath}||${sourceItem?.text}`
                                              : sourceItem.id + '||' + familyId,
                                      parentNodeId:
                                          sourceItem?.parentNodeId ?? '',
                                      children: (!selectedFile?.selectedProductTypeName
                                          ? selectedPTs
                                          : [
                                                ...(associationList?.filter(
                                                    (item) =>
                                                        item
                                                            ?.productTypeName?.[0] ===
                                                            selectedFile?.selectedProductTypeName &&
                                                        item?.treePath ===
                                                            sourceItem?.treePath
                                                )
                                                    ? selectedPTs?.filter(
                                                          (item) => {
                                                              return (
                                                                  item?.name ===
                                                                      selectedFile?.selectedProductTypeName &&
                                                                  sourceItem?.elementType ===
                                                                      'ASSEMBLY' &&
                                                                  associationList?.filter(
                                                                      (
                                                                          itemInner
                                                                      ) =>
                                                                          itemInner?.treePath ===
                                                                              sourceItem?.treePath &&
                                                                          itemInner
                                                                              ?.productTypeName?.[0] ===
                                                                              selectedFile?.selectedProductTypeName
                                                                  )?.length !==
                                                                      0
                                                              )
                                                          }
                                                      )!
                                                    : []!),
                                                ...nonSelectedPTs?.filter(
                                                    (item) =>
                                                        item?.associations?.some(
                                                            (itemInner) => {
                                                                return (
                                                                    itemInner?.treePath ===
                                                                        sourceItem?.treePath &&
                                                                    selectedFile?.productTypeInfo?.filter(
                                                                        (
                                                                            itemA
                                                                        ) =>
                                                                            itemA?.name !==
                                                                                selectedFile?.selectedProductTypeName &&
                                                                            itemA?.associations?.some(
                                                                                (
                                                                                    itemFile
                                                                                ) =>
                                                                                    itemFile?.treePath ===
                                                                                    sourceItem?.treePath
                                                                            )
                                                                    )
                                                                )
                                                            }
                                                        )
                                                )!,
                                            ]
                                      )?.map((itemProduct) => {
                                          return {
                                              baseElementId: 'none',
                                              elementId: 'none',
                                              elementType: 'PRODUCTTYPE',
                                              hasSubItems: false,
                                              id:
                                                  selectedElementFormat ===
                                                  'DIVISIONS'
                                                      ? `${divisionParentTreePath}||${sourceItem?.text}`
                                                      : sourceItem.id +
                                                        '||' +
                                                        familyId +
                                                        '||' +
                                                        itemProduct.name,
                                              isInProject: false,
                                              isL4L5Assembly: false,
                                              isLastSubAssembly: false,
                                              isSearchResult: false,
                                              isSelectedForAssociation: false,
                                              selected: false,
                                              subtitle: 'Product Type',
                                              title: itemProduct.name,
                                              isAddedForAssociation: true,
                                          }
                                      }),
                                      baseElementId: 'none',
                                      selected: false,
                                      title:
                                          selectedFile?.title?.split(
                                              selectedFile?.familyId!
                                          )?.[1] ?? selectedFile?.title,
                                      subtitle:
                                          'Family ' + selectedFile?.familyId,
                                      isSelectedForAssociation: true,
                                      hasSubItems:
                                          selectedFile?.productTypeInfo
                                              ?.length !== 0,
                                      isInProject: sourceItem.isInProject,
                                      elementType: 'PRODUCTFAMILY',
                                      elementId: sourceItem.id ?? '',
                                      isSearchResult: false,
                                      isL4L5Assembly: false,
                                      isLastSubAssembly: false,
                                      isAddedForAssociation: true,
                                  },
                              ]
                            : [],

                    title: elementTitle,
                    subtitle: subtitleText,
                    isSelectedForAssociation:
                        associationList?.findIndex(
                            (item) =>
                                item?.treePath?.split(
                                    `||${selectedFile?.familyId?.replaceAll(
                                        /['"]+/g,
                                        ''
                                    )}`
                                )?.[0] === sourceItem.treePath!
                        ) > -1,
                    hasSubItems:
                        (calledFromImportPage &&
                            associationList?.findIndex(
                                (item) =>
                                    item?.treePath?.split(
                                        `||${selectedFile?.familyId?.replaceAll(
                                            /['"]+/g,
                                            ''
                                        )}`
                                    )?.[0] === sourceItem.treePath!
                            ) > -1) ||
                        parentForSiblingsSelected?.some((item) =>
                            item?.includes(sourceItem?.text)
                        )
                            ? true
                            : sourceItem.hasChildren,
                    isInProject: sourceItem?.isInProject,
                    elementType: sourceItem.elementType,
                    emptyWithoutAssociations:
                        !(
                            associationList?.findIndex(
                                (item) =>
                                    item?.treePath?.split(
                                        `||${selectedFile?.familyId?.replaceAll(
                                            /['"]+/g,
                                            ''
                                        )}`
                                    )?.[0] === sourceItem.treePath!
                            ) > -1
                        ) &&
                        parentForSiblingsSelected?.some((item) =>
                            item?.includes(sourceItem?.text)
                        ),
                    elementId: sourceItem.id ?? '',
                    isSearchResult: sourceItem.isSearchResult ?? false,
                    isL4L5Assembly: isL4L5Assembly,
                    isLastSubAssembly: sourceItem.isLastSubAssembly,
                    isBestPractice: sourceItem.isBestPractice,
                    isGlobalBestPractice: sourceItem.isGlobalBestPractice,
                    isProjectOnly: sourceItem.isProjectOnly,
                    hasChildAssociation: parentAssembliesSelected.includes(
                        sourceItem.text
                    ),
                    hasSiblingAssociation: parentForSiblingsSelected?.some(
                        (item) => item?.includes(sourceItem.text)
                    ),
                    isBasedFromManufacturerOption:
                        sourceItem.isBasedFromManufacturerOption,
                    elementAddedToProjectId: sourceItem.elementId ?? undefined,
                    iteration: sourceItem.iteration,
                    isFromPPLTemplate: sourceItem.isFromPPLTemplate,
                }

                if (currentItemLevel > previousElementNestingLevel) {
                    //it's a child
                    if (horizontalRef[horizontalIndex].children === undefined) {
                        horizontalRef[horizontalIndex].children = []
                    }

                    horizontalRef[horizontalIndex].children?.push(elementItem)

                    if (
                        horizontalRef[horizontalIndex]
                            .isSelectedForAssociation &&
                        calledFromImportPage &&
                        associationList &&
                        (selectedPTs?.length !== 0 ||
                            !selectedFile?.selectedProductTypeName)
                    ) {
                        const familyId = `${
                            horizontalRef[horizontalIndex].id
                        }||${selectedFamilyId}||${selectedFile?.title?.replace(
                            selectedFamilyId!,
                            ''
                        )}`

                        horizontalRef[horizontalIndex].children?.unshift({
                            id:
                                selectedElementFormat === 'DIVISIONS'
                                    ? `${divisionParentTreePath}||${sourceItem?.text}`
                                    : familyId,
                            parentNodeId: sourceItem?.parentNodeId ?? '',
                            children: (!selectedFile?.selectedProductTypeName
                                ? selectedPTs
                                : [
                                      ...((selectedElementIdentifier &&
                                          selectedElementIdentifier?.treePath ===
                                              sourceItem?.treePath) ||
                                      selectedProjectNode?.id ===
                                          sourceItem?.id ||
                                      selectedProjectNode?.elementId ===
                                          sourceItem?.elementId ||
                                      associationList?.filter(
                                          (item) =>
                                              sourceItem?.elementType ===
                                                  'ASSEMBLY' &&
                                              item?.productTypeName?.[0] ===
                                                  selectedFile?.selectedProductTypeName
                                      )
                                          ? selectedPTs?.filter((item) => {
                                                return (
                                                    item?.name ===
                                                        selectedFile?.selectedProductTypeName &&
                                                    sourceItem?.elementType ===
                                                        'ASSEMBLY' &&
                                                    associationList?.filter(
                                                        (itemInner) =>
                                                            itemInner?.treePath ===
                                                                sourceItem?.treePath &&
                                                            itemInner
                                                                ?.productTypeName?.[0] ===
                                                                selectedFile?.selectedProductTypeName
                                                    )?.length !== 0
                                                )
                                            })!
                                          : []!),
                                      ...nonSelectedPTs?.filter((item) =>
                                          item?.associations?.some(
                                              (itemInner) => {
                                                  return (
                                                      itemInner?.treePath ===
                                                          sourceItem?.treePath &&
                                                      selectedFile?.productTypeInfo?.filter(
                                                          (itemA) =>
                                                              itemA?.name !==
                                                                  selectedFile?.selectedProductTypeName &&
                                                              itemA?.associations?.some(
                                                                  (itemFile) =>
                                                                      itemFile?.treePath ===
                                                                      sourceItem?.treePath
                                                              )
                                                      )
                                                  )
                                              }
                                          )
                                      )!,
                                  ]
                            )?.map((itemProduct) => {
                                return {
                                    baseElementId: 'none',
                                    elementId: 'none',
                                    elementType: 'PRODUCTTYPE',
                                    hasSubItems: false,
                                    id:
                                        selectedElementFormat === 'DIVISIONS'
                                            ? `${divisionParentTreePath}||${sourceItem?.text}`
                                            : familyId +
                                              '||' +
                                              itemProduct.name,
                                    isInProject: false,
                                    isL4L5Assembly: false,
                                    isLastSubAssembly: false,
                                    isSearchResult: false,
                                    isSelectedForAssociation: false,
                                    selected: false,
                                    subtitle: 'Product Type',
                                    title: itemProduct.name,
                                    isAddedForAssociation: true,
                                }
                            }),
                            baseElementId: 'none',
                            selected: false,
                            title:
                                selectedFile?.title?.split(
                                    selectedFile?.familyId!
                                )?.[1] ?? selectedFile?.title,
                            subtitle: 'Family ' + selectedFile?.familyId,
                            isSelectedForAssociation: true,
                            hasSubItems:
                                selectedFile?.productTypeInfo?.length !== 0,
                            isInProject: sourceItem.isInProject,
                            elementType: 'PRODUCTFAMILY',
                            elementId: sourceItem.id ?? '',
                            isSearchResult: false,
                            isL4L5Assembly: false,
                            isLastSubAssembly: false,
                            isAddedForAssociation: true,
                        })
                    }
                    horizontalIndex += 1
                    horizontalRef[horizontalIndex] = elementItem
                } else if (currentItemLevel === previousElementNestingLevel) {
                    //siblings!
                    horizontalIndex -= 1
                    horizontalRef[horizontalIndex].children?.push(elementItem)

                    horizontalIndex += 1
                    horizontalRef[horizontalIndex] = elementItem
                } else {
                    //look for nearest possbible parent
                    let counter = horizontalIndex
                    while (counter > 0) {
                        if (
                            elementItem.id?.startsWith(
                                horizontalRef[counter].id ?? ''
                            )
                        ) {
                            break
                        }
                        counter--
                    }

                    horizontalIndex = counter
                    horizontalRef[horizontalIndex].children?.push(elementItem)

                    horizontalIndex += 1
                    horizontalRef[horizontalIndex] = elementItem
                }
            }
        )
        return horizontalRef[0].children ?? null
    }, [
        elementList,
        selectedElementIdentifier,
        selectedFile,
        associationList,
        selectedElementFormat,
        calledFromImportPage,
        selectedProjectNode,
        calledFromImportDuplicateFamily,
    ])
    const handleOnLoadChildren = React.useCallback(
        (id: string, isBulkSelectRequested?: boolean) => {
            onLoadChildren(id, isBulkSelectRequested)
            if (elementList) {
                const elementIndex =
                    elementList.findIndex((element) => element.id === id) + 1
                const elementType =
                    elementList[elementIndex]?.elementType === 'ASSEMBLY'
                        ? 'PRODUCTFAMILY'
                        : 'PRODUCTTYPE'
                const firstHalf = (showFullLoadState ? [] : elementList).slice(
                    0,
                    elementIndex
                )
                const secondHalf = (showFullLoadState ? [] : elementList).slice(
                    elementIndex
                )
                const isLastSubAssembly =
                    elementList[elementIndex - 1]?.isLastSubAssembly
                // set skeleton count based on workspace toggle.
                const count = isBetaWorkspaceToggleEnabled ? 1 : 2
                const skeletonArray =
                    contentTeamFromStudioPage &&
                    isLastSubAssembly &&
                    !pathname.includes('workspace')
                        ? []
                        : Array.from(new Array(count)).map(
                              (_, index: number) =>
                                  ({
                                      treePath: `loadSkeleton-${index}`,
                                      text: `${elementList[elementIndex]?.text}|`,
                                      elementType,
                                      id: `loadSkeleton-${index}`,
                                      parentNodeId: `loadingSkeletonParent`,
                                  } as ProjectElementForImport)
                          )
                const result = firstHalf
                    .concat(skeletonArray)
                    .concat(secondHalf)

                setElementList(
                    selectedElementFormat === 'DIVISIONS'
                        ? updatedDivisionElements(result)?.filter(
                              (ele, ind) =>
                                  ind ===
                                  updatedDivisionElements(
                                      result ?? []
                                  )?.findIndex(
                                      (elem) =>
                                          elem.text === ele.text &&
                                          elem?.parentTreePath ===
                                              ele?.parentTreePath
                                  )
                          ) ?? []
                        : result
                )
            }
        },
        [
            onLoadChildren,
            elementList,
            showFullLoadState,
            selectedElementFormat,
            updatedDivisionElements,
            pathname,
            isBetaWorkspaceToggleEnabled,
        ]
    )
    const closeImportRemoveDialog = React.useCallback(() => {
        setShowRemoveDialog(false)
        setSelectedElementToRemove(undefined)
    }, [])
    const confirmRemoveElementFromImport = React.useCallback(
        (_: React.MouseEvent<Element, MouseEvent>) => {
            if (selectedElementToRemove) {
                handleChangeAssociation(selectedElementToRemove)
            }
        },
        [selectedElementToRemove]
    )
    const scrollToAdjustHeight = (scrollToAdd: number) => {
        const elementCardWrapper = document.getElementById(
            'elementTreeViewContainer'
        )
        if (elementCardWrapper && calledFromImportPage) {
            const scrollPosition =
                elementCardWrapper.children?.[0].children?.[0]?.scrollTop

            setTimeout(() => {
                elementCardWrapper.children[0].children[0].scrollTop =
                    scrollPosition + scrollToAdd
            }, 100)
        }
    }
    const handleSelectTreePath = React.useCallback(
        (selectedElement: ElementItemDataNode) => {
            const treePath = selectedFile?.selectedProductTypeName
                ? selectedElement.id
                : `${
                      selectedElement.id
                  }||${selectedFamilyId}|${selectedFile?.title
                      ?.split(selectedFamilyId!)?.[1]
                      ?.trim()}`

            if (selectedFile?.selectedProductTypeName) {
                const associationFamilyLevel = selectedFile?.associations?.filter(
                    (item) =>
                        item?.familyId === selectedFile?.familyId &&
                        item?.treePath ===
                            `${selectedElement?.id}||${
                                item?.familyId
                            }|${item?.familyName?.replace(
                                item?.familyId + ' ',
                                ''
                            )}`
                )

                if (
                    associationFamilyLevel &&
                    associationFamilyLevel?.length !== 0 &&
                    !selectedElement?.isSelectedForAssociation
                ) {
                    showGlobalSnackbar?.({
                        variables: {
                            message:
                                selectedFile?.selectedProductTypeName +
                                ' is already added to ' +
                                selectedElement?.title,
                            variant: 'error',
                            id: 'ProjectImportPage_PT_Already_Selected',
                        },
                    })
                    return
                }
            } else if (!selectedFile?.selectedProductTypeName) {
                let duplicatePaths: CheckForDuplicateFilesInAssemblyInput[] = []
                selectedFile?.productTypeInfo?.map((item) => {
                    if (item.associations && item.associations.length !== 0) {
                        return item.associations.map((itemInner) => {
                            duplicatePaths.push(itemInner!)
                        })!
                    }
                })
                const selectedPTs = duplicatePaths.filter(
                    (item) =>
                        item?.treePath?.split(
                            `||${selectedFile?.familyId?.replaceAll(
                                /['"]+/g,
                                ''
                            )}`
                        )?.[0] === selectedElement.id!
                )
                if (
                    selectedPTs?.length !== 0 &&
                    !selectedElement?.isSelectedForAssociation
                ) {
                    const duplicateProductTypesText = selectedPTs
                        ?.map((item, index) => {
                            if (selectedPTs.length === 1) {
                                return item.productTypeName?.[0]
                            } else if (
                                selectedPTs.length > 1 &&
                                index <= selectedPTs.length - 2
                            ) {
                                return item.productTypeName?.[0]
                            }
                        })
                        ?.join(', ')
                        .replace(/,\s*$/, '')

                    showGlobalSnackbar?.({
                        variables: {
                            message: `${
                                selectedPTs.length === 1
                                    ? selectedPTs?.[0]?.productTypeName?.[0]
                                    : duplicateProductTypesText + ' and'
                            }  ${
                                selectedPTs.length > 1
                                    ? selectedPTs?.[selectedPTs.length - 1]
                                          ?.productTypeName?.[0]
                                    : ''
                            } ${
                                selectedPTs?.length === 1 ? 'is' : 'are'
                            } already added to ${selectedElement?.title}`,
                            variant: 'error',
                            id: 'ProjectImportPage_PT_Already_Selected',
                        },
                    })
                    return
                }
            }
            scrollToAdjustHeight(1)

            if (associationList.length === 0) {
                setAssociationList([
                    {
                        treePath: treePath,
                        elementId: selectedElement.elementId,
                        familyName: selectedFile?.title,
                        familyId: selectedFamilyId,
                        assemblyName: selectedElement?.title,
                        hasSubItems: selectedElement.hasSubItems,
                        emptyWithoutAssociations:
                            selectedElement?.emptyWithoutAssociations,
                        productTypeName: selectedFile?.selectedProductTypeName
                            ? [selectedFile?.selectedProductTypeName]
                            : undefined,
                    },
                ])
                getAssociationLists?.(
                    [
                        {
                            treePath: treePath,
                            elementId: selectedElement.elementId,
                            familyName: selectedFile?.title,
                            hasSubItems: selectedElement.hasSubItems,
                            familyId: selectedFamilyId,
                            assemblyName: selectedElement?.title,
                            emptyWithoutAssociations:
                                selectedElement?.emptyWithoutAssociations,
                            productTypeName: selectedFile?.selectedProductTypeName
                                ? [selectedFile?.selectedProductTypeName]
                                : undefined,
                        },
                    ],
                    selectedFile?.selectedProductTypeName !== undefined
                )
            } else {
                const itemToMatch = selectedFile?.selectedProductTypeName
                    ? selectedElement?.id!
                    : treePath!

                if (
                    associationList.findIndex(
                        (item) => item?.treePath === itemToMatch
                    ) > -1
                ) {
                    setAssociationList(
                        associationList.filter(
                            (item) => !item?.treePath?.includes(itemToMatch!)
                        )
                    )
                    getAssociationLists?.(
                        associationList.filter(
                            (item) => !item?.treePath?.includes(itemToMatch!)
                        ),
                        selectedFile?.selectedProductTypeName !== undefined,
                        !selectedFile?.selectedProductTypeName
                            ? treePath!
                            : undefined
                    )
                    onSelectedItemIdChange?.(undefined)
                } else {
                    setAssociationList(
                        [
                            ...associationList,
                            {
                                treePath: treePath,
                                elementId: selectedElement.elementId,
                                familyName: selectedFile?.title,
                                emptyWithoutAssociations:
                                    selectedElement?.emptyWithoutAssociations,
                                familyId: selectedFamilyId,
                                hasSubItems: selectedElement.hasSubItems,
                                assemblyName: selectedElement?.title,
                                productTypeName: selectedFile?.selectedProductTypeName
                                    ? [selectedFile?.selectedProductTypeName]
                                    : undefined,
                            },
                        ]?.filter(
                            (item, index, nextItem) =>
                                nextItem.findIndex(
                                    (item2) =>
                                        item2?.elementId === item?.elementId
                                ) === index
                        )
                    )
                    getAssociationLists?.(
                        [
                            ...associationList,
                            {
                                treePath: treePath,
                                elementId: selectedElement.elementId,
                                familyName: selectedFile?.title,
                                familyId: selectedFamilyId,
                                assemblyName: selectedElement?.title,
                                emptyWithoutAssociations:
                                    selectedElement?.emptyWithoutAssociations,
                                hasSubItems: selectedElement.hasSubItems,
                                productTypeName: selectedFile?.selectedProductTypeName
                                    ? [selectedFile?.selectedProductTypeName]
                                    : undefined,
                            },
                        ]?.filter(
                            (item, index, nextItem) =>
                                nextItem.findIndex(
                                    (item2) =>
                                        item2?.elementId === item?.elementId
                                ) === index
                        ),
                        selectedFile?.selectedProductTypeName !== undefined
                    )
                }
            }
            setShowRemoveDialog(false)
            setSelectedElementToRemove(undefined)
        },
        [associationList, selectedFile, getAssociationLists, showGlobalSnackbar]
    )
    const handleChangeAssociation = React.useCallback(
        (selectedElement: ElementItemDataNode) => {
            if (
                !selectedElement.isLastSubAssembly ||
                selectedElement?.elementType !== 'ASSEMBLY' ||
                (!projectId && calledFromImportPage && selectedFile?.imported)
            )
                return
            const isDefaultAssociation: boolean =
                selectedFile?.parentAssemblyTreePaths?.some(
                    (item) => item === selectedElement?.id
                ) ?? false
            if (isDefaultAssociation) {
                showGlobalSnackbar?.({
                    variables: {
                        message:
                            'This assembly association is part of the MasterSpec core associations and can not be removed as part of a project.',
                        variant: 'error',
                        id: 'ProjectImportPage_Default_TreePath',
                    },
                })
            } else {
                const familyToRemove = selectedElement?.children?.filter(
                    (item) => item?.title === selectedFile?.title
                )?.[0]
                const ptItemToRemove = familyToRemove?.children?.filter(
                    (itemChild) =>
                        itemChild?.title ===
                            selectedFile?.selectedProductTypeName &&
                        itemChild?.isInProject
                )
                const itemToRemoveAdded = selectedFile?.selectedProductTypeName
                    ? ptItemToRemove?.length !== 0
                    : familyToRemove?.isInProject
                const checkAddedItem =
                    selectedElement?.children?.length === 0
                        ? selectedElement?.isInProject
                        : itemToRemoveAdded
                if (
                    checkAddedItem &&
                    !isBPImport &&
                    !showRemoveDialog &&
                    !selectedElementToRemove &&
                    associationList.findIndex(
                        (item) =>
                            item?.treePath?.split(
                                '||' + selectedFile?.familyId
                            )?.[0] === selectedElement.id
                    ) > -1
                ) {
                    setSelectedElementToRemove(selectedElement)
                    setShowRemoveDialog(true)
                } else {
                    handleSelectTreePath(selectedElement)
                }
            }
        },
        [
            associationList,
            getAssociationLists,
            selectedFile,
            showGlobalSnackbar,
            selectedElementToRemove,
            showRemoveDialog,
            calledFromImportPage,
            projectId,
            isBPImport,
        ]
    )
    React.useEffect(() => {
        setElementList(
            selectedElementFormat === 'DIVISIONS'
                ? updatedDivisionElements(elementFlatList ?? [])
                : elementFlatList
        )
    }, [elementFlatList, selectedElementFormat, updatedDivisionElements])

    /**
     * Watch PT activation store and update element list when PT nodes are activated
     */
    usePTElementNodesUpdate({
        elementList,
        setElementList,
        selectedElementFormat,
        isBetaWorkspaceToggleEnabled,
        isOMWorkspace,
        contentTeamFromStudioPage,
        isFromWorkSpacePage,
    })

    /**
     * Fetches the OM version dropdown options based on the project ID and family number.
     * This effect hook is triggered when the 'isOMWorkspace' and 'familyNumber' state variables change.
     *
     * @param {boolean} isOMWorkspace - Indicates whether the current workspace is on OM workspace.
     * @param {string} familyNumber - The family number for which the OM version dropdown options need to be fetched.
     * @param {string} projectId - The project ID for which the OM version dropdown options need to be fetched.
     * @param {fetchOMVersionDropdown} fetchOMVersionDropdown - A function to fetch the OM version dropdown options.
     */
    // TODO: Implement the useVersion custom hook
    React.useEffect(() => {
        if (isOMWorkspace) {
            fetchOMVersionDropdown({
                variables: {
                    input: {
                        projectId,
                        familyNumber,
                        familyName,
                    },
                },
            })
        }
    }, [isOMWorkspace, familyNumber])

    /**
     * This effect hook is used to set the selected OM version based on the project ID, family number, and the current state of the 'isOMWorkspace' flag.
     * It also updates the 'selectedOMVersion' state variable with the found OM version dropdown option.
     *
     * @param {boolean} isOMWorkspace - Indicates whether the current workspace is on OM workspace.
     * @param {ElementItemIdentifier | undefined} selectedElementIdentifier - Indicates the selected element on the element tree.
     * @param {FamilyIteration[]} omDropdownOptions - An array of OM version dropdown options.
     * @param {FamilyIteration | undefined} selectedOMVersion - The currently selected OM version dropdown option.
     * @param {React.Dispatch<React.SetStateAction<FamilyIteration | undefined>>} setSelectedOMVersion - A function to update the 'selectedOMVersion' state variable.
     */
    // TODO: Implement the useVersion custom hook
    React.useEffect(() => {
        if (
            isOMWorkspace &&
            selectedItemIdFromStudioTable &&
            !!omDropdownOptions.length
        ) {
            setSelectedOMVersion(
                omDropdownOptions?.find(
                    (option) =>
                        option?.elementNodes[0]?.id ===
                        selectedItemIdFromStudioTable
                )
            )
        }
    }, [isOMWorkspace, selectedItemIdFromStudioTable, omDropdownOptions])

    React.useEffect(() => {
        if (selectedFile) {
            if (selectedFile.selectedProductTypeName) {
                const selectedPTAssociations = selectedFile?.productTypeInfo?.filter(
                    (item) =>
                        item.name === selectedFile?.selectedProductTypeName
                )?.[0]?.associations
                setAssociationList(selectedPTAssociations ?? [])
            } else {
                if (
                    selectedFile?.parentAssemblyTreePaths?.length !== 0 &&
                    selectedFile?.associations &&
                    selectedFile?.parentAssemblyTreePaths
                ) {
                    setAssociationList([
                        ...(selectedFile?.associations?.filter(function (
                            value,
                            index,
                            array
                        ) {
                            return array.indexOf(value) === index
                        }) ?? []),
                        ...(selectedFile?.parentAssemblyTreePaths ?? [])?.map(
                            (item) => {
                                return {
                                    treePath:
                                        item +
                                        '||' +
                                        selectedFile?.familyId +
                                        '|' +
                                        selectedFile?.title?.replace(
                                            `${selectedFile?.familyId}${' '}`,
                                            ''
                                        ),
                                    familyName: selectedFile?.title,
                                    isForReplacement: true,
                                    elementId:
                                        '8d4bd76b-11ab-44d2-af8c-f82f5d63598f',
                                    productTypeName: undefined,
                                    assemblyName: item?.split('||')?.[
                                        item?.split('||')?.length - 1
                                    ],
                                    familyId: selectedFile?.familyId,
                                }
                            }
                        )!,
                    ])
                    getAssociationLists?.(
                        [
                            ...(selectedFile?.associations?.filter(function (
                                value,
                                index,
                                array
                            ) {
                                return array.indexOf(value) === index
                            }) ?? [])!,
                            ...(
                                selectedFile?.parentAssemblyTreePaths ?? []
                            )?.map((item) => {
                                return {
                                    treePath:
                                        item +
                                        '||' +
                                        selectedFile?.familyId +
                                        '|' +
                                        selectedFile?.title?.replace(
                                            `${selectedFile?.familyId}${' '}`,
                                            ''
                                        ),
                                    familyName: selectedFile?.title,
                                    isForReplacement: true,
                                    elementId: '',
                                    productTypeName: undefined,
                                    familyId: selectedFile?.familyId,
                                }
                            })!,
                        ],
                        selectedFile?.selectedProductTypeName !== undefined
                    )
                } else {
                    setAssociationList(
                        selectedFile?.associations?.filter(function (
                            value,
                            index,
                            array
                        ) {
                            return array.indexOf(value) === index
                        })! ?? []
                    )
                    getAssociationLists?.(
                        selectedFile?.associations?.filter(function (
                            value,
                            index,
                            array
                        ) {
                            return array.indexOf(value) === index
                        })! ?? [],
                        selectedFile?.selectedProductTypeName !== undefined
                    )
                }
            }
        }
    }, [selectedFile])

    React.useEffect(() => {
        if (selectedFileData) {
            const fileData = {
                ...selectedFileData,
                associations: selectedFileData?.associations?.filter(
                    (item, index, nextItem) =>
                        nextItem.findIndex(
                            (item2) => item2?.treePath === item?.treePath
                        ) === index
                ),
            }
            setSelectedFile(fileData)
        }
    }, [selectedFileData])

    const handleResize = React.useCallback(
        (width: number) => setPanelWidth(width),
        []
    )

    const handleOMVersionSelectChange = React.useCallback(
        (value: ProductFamilies[] | FamilyIteration) => {
            const currentValue = value as FamilyIteration
            if (
                selectedOMVersion?.iterationName === currentValue?.iterationName
            ) {
                return showGlobalSnackbarHook?.({
                    variables: {
                        message: 'Selected version is already loaded',
                        variant: 'error',
                        id: `VersionSwitch_UpdateError`,
                    },
                })
            }
            setSelectedOMVersion(currentValue)

            if (!currentValue?.elementNodes || !elementList) return

            const selectedElementNodes = getSortedList(
                currentValue.elementNodes,
                'nodeOrder'
            )
            const parentTreePath = selectedElementNodes[0]?.parentTreePath

            const currentReplacementNodeIndex = elementList.findIndex(
                (item) => item?.treePath === parentTreePath
            )

            if (currentReplacementNodeIndex !== -1) {
                const updatedElementList = [
                    ...elementList.slice(0, currentReplacementNodeIndex + 1),
                    ...selectedElementNodes,
                ]

                setElementList(updatedElementList)
                setCategoryElementStates({
                    all: {
                        ...categoryElementStates['all'],
                        data: updatedElementList,
                    },
                })

                const { baseElementId, id, treePath } = selectedElementNodes[0]
                setSelectedElementIdentifier({
                    baseElementId,
                    id,
                    treePath,
                })

                const timeout = setTimeout(() => {
                    showGlobalSnackbarHook?.({
                        variables: {
                            message: 'Switched Version Successfully',
                            variant: 'success',
                            id: `VersionSwitch_UpdateSuccess`,
                        },
                    })
                    clearTimeout(timeout)
                }, 100)
            }
        },
        [selectedOMVersion, elementList]
    )

    return (
        <div
            className={classes.elementPanelContentContainer}
            key={`panel-${panelTitle ?? 'Project Elements'}${redirectKey}`}
        >
            <GridContainer
                direction="column"
                className={classes.panelContentGrid}
            >
                {(calledFromImportPage || calledFromImportDuplicateFamily) && (
                    <GridItem className={classes.panelTitleContainerImport}>
                        <Typography
                            variant="h3"
                            className={classes.panelTitleImport}
                            data-testid="elementsPanelTitle"
                        >
                            {panelTitle}
                        </Typography>
                        <Typography
                            variant="h3"
                            className={classes.panelTitleImport}
                        >
                            SET
                        </Typography>
                    </GridItem>
                )}
                <ReactResizeDetector
                    handleHeight={false}
                    handleWidth={true}
                    onResize={handleResize}
                />
                {contentTeamFromStudioPage && (
                    <GridItem
                        className={clsx(
                            classes.panelTitleContainerImport,
                            classes.panelTitleContainerStudio
                        )}
                    >
                        {pathname.includes('workspace') ? (
                            <>
                                <Typography
                                    variant="h3"
                                    className={clsx(
                                        classes.panelTitle,
                                        classes.panelTitleStudio
                                    )}
                                    data-testid="elementsPanelTitle"
                                ></Typography>
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant="h3"
                                    className={clsx(
                                        classes.panelTitle,
                                        classes.panelTitleStudio
                                    )}
                                    data-testid="elementsPanelTitle"
                                >
                                    {panelTitle}
                                </Typography>
                                <ElementPanelSearch
                                    panelTitle={panelTitle}
                                    setBPSearchValue={setBPSearchValue}
                                    width={panelWidth}
                                />
                            </>
                        )}
                    </GridItem>
                )}
                {!!elementList && elementList.length === 0 && !isLoading && (
                    <GridItem container justifyContent="center">
                        <Typography
                            component="span"
                            className={classes.emptyElementsListText}
                        >
                            No Results Found.
                            <br />
                            Try Again.
                        </Typography>
                    </GridItem>
                )}
                {((elementList?.length ?? 0) > 0 || isLoading) && (
                    <GridItem className={classes.elementTreeViewContainer}>
                        <ElementTreeView
                            isLoading={
                                contentTeamFromStudioPage ? false : isLoading
                            }
                            showFullLoadState={showFullLoadState}
                            isNewDataLoading={isNewDataLoading}
                            showLoadMoreButton={hasMoreElementsToFetch}
                            onLoadMoreClick={onLoadMoreClick}
                            parentNodeIdPendingBulkSelect={
                                parentNodeIdPendingBulkSelect
                            }
                            handleBPElementTreeChange={
                                handleBPElementTreeChange
                            }
                            sourceTree={treeStruct ?? []}
                            selectedElementIdentifier={
                                selectedElementIdentifier
                            }
                            onSelectedElementIdentifierChange={
                                onSelectedItemIdChange
                            }
                            savePreviousElementForPersistance={
                                savePreviousElementForPersistance
                            }
                            selectedElementFormat={selectedElementFormat}
                            selectedFile={selectedFile}
                            handleChangeAssociation={handleChangeAssociation}
                            calledFromImportPage={calledFromImportPage}
                            totalElementCount={totalElementCount}
                            totalFetchedElementCount={elementList?.length ?? 0}
                            famNumberFormat={famNumberFormat}
                            isCollapsed={panelTitle.includes('Search Results')}
                            searchFilters={searchFilters}
                            onLoadChildren={handleOnLoadChildren}
                            associationList={associationList}
                            contentTeamFromStudioPage={
                                contentTeamFromStudioPage
                            }
                            fromStudio={fromStudio}
                            selectedBulkAddElements={selectedBulkAddElements}
                            setSelectedBulkAddElements={
                                setSelectedBulkAddElements
                            }
                            projectId={projectId}
                            isFromWorkSpacePage={isFromWorkSpacePage}
                            persistedElement={persistedElement}
                            calledFromImportDuplicateFamily={
                                calledFromImportDuplicateFamily
                            }
                            handleSelectDuplicateFamilyVersion={
                                handleSelectDuplicateFamilyVersion
                            }
                            selectedDuplicateFamilyVersions={
                                selectedDuplicateFamilyVersions
                            }
                            omDropdownOptions={omDropdownOptions}
                            handleOMVersionSelectChange={
                                handleOMVersionSelectChange
                            }
                            isOMWorkspace={isOMWorkspace}
                            selectedOMVersion={selectedOMVersion}
                            updateElementList={updateElementList}
                            isBetaWorkspaceToggleEnabled={
                                isBetaWorkspaceToggleEnabled
                            }
                        />
                    </GridItem>
                )}
            </GridContainer>
            <ConfirmationDialog
                openDialog={showRemoveDialog}
                handleDialogClose={closeImportRemoveDialog}
                dialogActionCallback={confirmRemoveElementFromImport}
                contentNode={confirmationDialogContent}
                size="small"
            />
        </div>
    )
}

export const getElementNestingLevel = (subTextCode: string, type: string) => {
    let currentItemLevel = 1

    switch (type) {
        case 'ASSEMBLY':
            //determine assembly level
            currentItemLevel = getAssemblyLevel(subTextCode)
            break
        case 'PRODUCTFAMILY':
            currentItemLevel = 4
            break
        case 'PRODUCTTYPE':
            currentItemLevel = 5
            break
    }

    return currentItemLevel
}

export const getAssemblyLevel = (subTextCode: string) => {
    //determine assembly level
    const assemblyNumberSplit = subTextCode.split('.')
    let assemblyLevel = 1
    if (assemblyNumberSplit.length > 1) {
        assemblyLevel += assemblyNumberSplit[1].length > 2 ? 2 : 1
    }

    return assemblyLevel
}

export const getSubtextCode = (elementText: string) => {
    const textSplit = elementText.split('|')
    const subTextCode = textSplit[0]
    return subTextCode
}

export default ElementsPanel
